import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Mail, MessageCircle, Save, Settings as SettingsIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultSettings = {
  is_enabled: false,
  inactive_days_first: 60,
  inactive_days_second: 61,
  discount_percentage: 10,
  send_email: true,
  send_whatsapp: true
};

const defaultTemplates = {
  email_template: {
    subject: 'Hoi {{customer_name}}, wir vermissen dich bei {{studio_name}}',
    body: 'Hoi {{customer_name}},\n\nwir haben gesehen, dass du seit {{days_inactive}} Tagen keinen Termin mehr bei uns hattest. Gibt es einen Grund, warum du keinen Termin gemacht hast?\n\nBuche dir direkt einen neuen Termin: {{portal_url}}\n\nLiebe Grüsse\nAlex'
  },
  whatsapp_template_first: {
    template_name: 'reaktivierung_30d_v1',
    body: 'Hoi {{customer_name}} 👋\n\ndein letzter Termin liegt schon {{days_inactive}} Tage zurück.\nWenn du wieder durchstarten möchtest, freuen wir uns, dich bald bei uns zu sehen 💪\n\nHier kannst du dir direkt deinen nächsten Termin buchen:\n{{portal_url}}\n\nLiebe Grüsse\nAlex',
    language: 'de'
  },
  whatsapp_template_second: {
    template_name: 'reaktivierung_60d_v1',
    body: 'Hoi {{customer_name}} 👋\n\ndein letzter Termin liegt schon {{days_inactive}} Tage zurück.\nAls kleines Dankeschön erhältst du {{discount_percent}}% Rabatt auf dein nächstes Abo.\nDein Rabattcode: {{discount_code}} (gültig 30 Tage)\n\nHier kannst du dir direkt deinen nächsten Termin buchen:\n{{portal_url}}\n\nLiebe Grüsse\nAlex',
    language: 'de'
  }
};

const ReEngagementSettingsPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(defaultSettings);
  const [templates, setTemplates] = useState(defaultTemplates);
  const [loading, setLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingTemplates, setSavingTemplates] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [settingsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/settings/re-engagement`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/re-engagement/templates`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setSettings({ ...defaultSettings, ...(settingsRes.data || {}) });
      setTemplates({
        email_template: {
          ...defaultTemplates.email_template,
          ...(templatesRes.data?.email_template || {})
        },
        whatsapp_template_first: {
          ...defaultTemplates.whatsapp_template_first,
          ...(templatesRes.data?.whatsapp_template_first || {})
        },
        whatsapp_template_second: {
          ...defaultTemplates.whatsapp_template_second,
          ...(templatesRes.data?.whatsapp_template_second || {})
        }
      });
    } catch (error) {
      toast.error('Fehler beim Laden der Re-Engagement Einstellungen');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/settings/re-engagement`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({ ...defaultSettings, ...(response.data || {}) });
      toast.success('Re-Engagement Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern der Einstellungen');
      console.error(error);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveTemplates = async () => {
    setSavingTemplates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/settings/re-engagement/templates`, templates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates({
        email_template: response.data?.email_template || templates.email_template,
        whatsapp_template_first: response.data?.whatsapp_template_first || templates.whatsapp_template_first,
        whatsapp_template_second: response.data?.whatsapp_template_second || templates.whatsapp_template_second
      });
      toast.success('Vorlagen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern der Vorlagen');
      console.error(error);
    } finally {
      setSavingTemplates(false);
    }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="reengagement-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <SettingsIcon size={28} />
            Re-Engagement
          </h1>
          <p className="text-gray-600 mt-2">
            Automatische Erinnerung an inaktive Kunden per E-Mail und WhatsApp.
          </p>
        </div>
        {!embedded && (
          <Button variant="outline" onClick={() => navigate('/communication/settings')} data-testid="reengagement-back-button">
            ← Zurück zu Kommunikation
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Status
            <Badge
              variant={settings.is_enabled ? 'default' : 'secondary'}
              data-testid="reengagement-status-badge"
            >
              {settings.is_enabled ? 'Aktiv' : 'Inaktiv'}
            </Badge>
          </CardTitle>
          <CardDescription>
            Aktiviere den Flow, um automatisch nach Inaktivität Erinnerungen zu senden.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between border rounded-lg p-4">
            <div>
              <p className="font-semibold">Re-Engagement aktivieren</p>
              <p className="text-sm text-gray-600">Automatisch nach Inaktivität kontaktieren</p>
            </div>
            <Switch
              checked={settings.is_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
              data-testid="reengagement-enabled-switch"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="inactive-first">Erste Erinnerung nach (Tage)</Label>
              <Input
                id="inactive-first"
                type="number"
                min="1"
                value={settings.inactive_days_first}
                onChange={(event) =>
                  setSettings({
                    ...settings,
                    inactive_days_first: parseInt(event.target.value || '0', 10)
                  })
                }
                data-testid="reengagement-first-days-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inactive-second">Zweite Erinnerung nach (Tage)</Label>
              <Input
                id="inactive-second"
                type="number"
                min="1"
                value={settings.inactive_days_second}
                onChange={(event) =>
                  setSettings({
                    ...settings,
                    inactive_days_second: parseInt(event.target.value || '0', 10)
                  })
                }
                data-testid="reengagement-second-days-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="discount-percent">Rabatt bei 2. Erinnerung (%)</Label>
              <Input
                id="discount-percent"
                type="number"
                min="1"
                max="100"
                value={settings.discount_percentage}
                onChange={(event) =>
                  setSettings({
                    ...settings,
                    discount_percentage: parseInt(event.target.value || '10', 10)
                  })
                }
                data-testid="reengagement-discount-percent-input"
              />
              <p className="text-xs text-gray-500">Rabattcode wird automatisch generiert</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between border rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Mail size={18} />
                <div>
                  <p className="font-semibold">E-Mail senden</p>
                  <p className="text-sm text-gray-600">Nur wenn E-Mail vorhanden</p>
                </div>
              </div>
              <Switch
                checked={settings.send_email}
                onCheckedChange={(checked) => setSettings({ ...settings, send_email: checked })}
                data-testid="reengagement-send-email-switch"
              />
            </div>
            <div className="flex items-center justify-between border rounded-lg p-4">
              <div className="flex items-center gap-2">
                <MessageCircle size={18} />
                <div>
                  <p className="font-semibold">WhatsApp senden</p>
                  <p className="text-sm text-gray-600">Nur wenn WA Template aktiv</p>
                </div>
              </div>
              <Switch
                checked={settings.send_whatsapp}
                onCheckedChange={(checked) => setSettings({ ...settings, send_whatsapp: checked })}
                data-testid="reengagement-send-whatsapp-switch"
              />
            </div>
          </div>

          <Button
            onClick={handleSaveSettings}
            disabled={savingSettings || loading}
            className="flex items-center gap-2"
            data-testid="reengagement-save-settings-button"
          >
            <Save size={16} />
            {savingSettings ? 'Speichert...' : 'Einstellungen speichern'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vorlagen & Automatisierung</CardTitle>
          <CardDescription>
            Konfiguriere E-Mail- und WhatsApp-Vorlagen sowie Automatisierungsregeln
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            onClick={() => navigate('/email/templates')}
            className="flex items-center justify-between border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
            data-testid="reengagement-link-email-templates"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="font-semibold">E-Mail Vorlagen</p>
                <p className="text-sm text-gray-500">2 Re-Engagement Templates (30 Tage + 60 Tage mit Rabatt)</p>
              </div>
            </div>
            <span className="text-gray-400">→</span>
          </div>

          <div
            onClick={() => navigate('/whatsapp/templates')}
            className="flex items-center justify-between border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
            data-testid="reengagement-link-whatsapp-templates"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <MessageCircle size={20} className="text-green-600" />
              </div>
              <div>
                <p className="font-semibold">WhatsApp Vorlagen (Meta)</p>
                <p className="text-sm text-gray-500">2 Meta Templates: reaktivierung_30d, reaktivierung_60d</p>
              </div>
            </div>
            <span className="text-gray-400">→</span>
          </div>

          <div
            onClick={() => navigate('/automation/rules')}
            className="flex items-center justify-between border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
            data-testid="reengagement-link-automation-rules"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <SettingsIcon size={20} className="text-orange-600" />
              </div>
              <div>
                <p className="font-semibold">Automatisierungsregeln</p>
                <p className="text-sm text-gray-500">Re-Engagement Regeln aktivieren/deaktivieren</p>
              </div>
            </div>
            <span className="text-gray-400">→</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReEngagementSettingsPage;
